import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';

export default function HilfeCenter() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // ... (Schema.org code remains the same) ...
    // Add Schema.org JSON-LD for FAQPage
    const faqItems = [
      {
        question: t('help.faq.q1'),
        answer: t('help.faq.a1')
      },
      {
        question: t('help.faq.q2'),
        answer: t('help.faq.a2')
      },
      {
        question: t('help.faq.q3'),
        answer: t('help.faq.a3')
      },
      {
        question: t('help.faq.q7'),
        answer: t('help.faq.a7')
      },
      {
        question: t('help.faq.q16'),
        answer: t('help.faq.a16')
      }
    ];

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    });
    document.head.appendChild(schemaScript);

    // Update page title
    document.title = `${t('help.faq.title')} | reputrust.ai Support`;

    return () => {
      if (schemaScript.parentNode) {
        schemaScript.parentNode.removeChild(schemaScript);
      }
    };
  }, [t]);

  const faqCategories = [
    {
      title: t('help.categories.gettingStarted'),
      icon: "ri-rocket-line",
      questions: [
        { q: t('help.faq.q1'), a: t('help.faq.a1') },
        { q: t('help.faq.q2'), a: t('help.faq.a2') },
        { q: t('help.faq.q3'), a: t('help.faq.a3') }
      ]
    },
    {
      title: t('help.categories.reviewManagement'),
      icon: "ri-star-line",
      questions: [
        { q: t('help.faq.q4'), a: t('help.faq.a4') },
        { q: t('help.faq.q5'), a: t('help.faq.a5') },
        { q: t('help.faq.q6'), a: t('help.faq.a6') }
      ]
    },
    {
      title: t('help.categories.aiResponse'),
      icon: "ri-robot-line",
      questions: [
        { q: t('help.faq.q7'), a: t('help.faq.a7') },
        { q: t('help.faq.q8'), a: t('help.faq.a8') },
        { q: t('help.faq.q9'), a: t('help.faq.a9') }
      ]
    },
    {
      title: t('help.categories.socialMedia'),
      icon: "ri-share-line",
      questions: [
        { q: t('help.faq.q10'), a: t('help.faq.a10') },
        { q: t('help.faq.q11'), a: t('help.faq.a11') },
        { q: t('help.faq.q12'), a: t('help.faq.a12') }
      ]
    },
    {
      title: t('help.categories.googleBusiness'),
      icon: "ri-google-line",
      questions: [
        { q: t('help.faq.q13'), a: t('help.faq.a13') },
        { q: t('help.faq.q14'), a: t('help.faq.a14') },
        { q: t('help.faq.q15'), a: t('help.faq.a15') }
      ]
    },
    {
      title: t('help.categories.pricing'),
      icon: "ri-money-euro-circle-line",
      questions: [
        { q: t('help.faq.q16'), a: t('help.faq.a16') },
        { q: t('help.faq.q17'), a: t('help.faq.a17') },
        { q: t('help.faq.q18'), a: t('help.faq.a18') }
      ]
    }
  ];

  // Filter categories and questions based on search query
  const filteredCategories = faqCategories.map(category => {
    // If the category matches, return all questions
    if (category.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return category;
    }

    // Otherwise, filter questions
    const filteredQuestions = category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.a.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Return category with filtered questions if any match
    if (filteredQuestions.length > 0) {
      return { ...category, questions: filteredQuestions };
    }

    return null;
  }).filter((category): category is NonNullable<typeof category> => category !== null);

  const quickLinks = [
    { icon: "ri-book-open-line", title: t('help.quickLinks.documentation'), desc: t('help.quickLinks.documentationDesc'), link: "/#funktionen" },
    { icon: "ri-video-line", title: t('help.quickLinks.videos'), desc: t('help.quickLinks.videosDesc'), link: "#" },
    { icon: "ri-customer-service-line", title: t('help.quickLinks.support'), desc: t('help.quickLinks.supportDesc'), link: "mailto:support@reputrust.ai" },
    { icon: "ri-calendar-line", title: t('help.quickLinks.demo'), desc: t('help.quickLinks.demoDesc'), link: "mailto:info@reputrust.ai?subject=Demo-Anfrage" }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-600 selection:bg-tron-primary/30 selection:text-[#0F172A]">
      <SEO titleKey="seo.title.help" descriptionKey="seo.description.help" />
      <Header />

      <div className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        {/* ... (Background code remains) ... */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#1E3A8A]/10 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#10B981]/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Hero Section */}
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 mb-20 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white border border-slate-200 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] mb-8 shadow-lg">
            <i className="ri-question-answer-line text-4xl"></i>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-[#0F172A] mb-6 leading-tight" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            {t('help.title')}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('help.subtitle')}
          </p>

          <div className="max-w-2xl mx-auto relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-emerald-400 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative">
              <input
                type="text"
                placeholder={t('help.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pl-14 bg-white border border-slate-200 rounded-xl text-[#0F172A] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all text-lg shadow-lg"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              />
              <i className="ri-search-2-line absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 text-xl group-focus-within:text-[#10B981] transition-colors"></i>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="max-w-[1280px] mx-auto px-6 mb-24 relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <a
                key={index}
                href={link.link}
                className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-blue-300 transition-all duration-300 group cursor-pointer hover:shadow-lg flex flex-col h-full shadow-sm"
              >
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center mb-4 border border-slate-100 group-hover:border-[#10B981]/30 transition-colors duration-300">
                  <i className={`${link.icon} text-slate-400 text-2xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors duration-300`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                  {link.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mt-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {link.desc}
                </p>
              </a>
            ))}
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="max-w-[1280px] mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              {t('help.faq.title')}
            </h2>
            <p className="text-lg text-slate-600" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('help.faq.subtitle')}
            </p>
          </div>

          <div className="grid gap-8">
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category, catIndex) => (
                <div key={catIndex} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-xl flex items-center justify-center border border-white/20 text-white shadow-md">
                      <i className={`${category.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                      {category.title}
                    </h3>
                  </div>

                  <div className="grid gap-4">
                    {category.questions.map((item, qIndex) => (
                      <details key={qIndex} className="group bg-slate-50 rounded-xl border border-slate-200 open:bg-white open:shadow-md open:border-blue-200 transition-all duration-300">
                        <summary className="flex items-center justify-between cursor-pointer p-5 list-none">
                          <span className="font-semibold text-slate-700 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors pr-8" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                            {item.q}
                          </span>
                          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-all transform group-open:rotate-180 group-open:bg-gradient-to-r group-open:from-[#10B981] group-open:to-[#1E3A8A] group-open:text-white group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] group-hover:text-white shadow-sm">
                            <i className="ri-arrow-down-s-line text-xl"></i>
                          </span>
                        </summary>
                        <div className="px-5 pb-5 pt-0">
                          <div className="h-px w-full bg-slate-200 mb-4"></div>
                          <p className="text-slate-600 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                            {item.a}
                          </p>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <i className="ri-search-line text-4xl text-slate-400 mb-4 block"></i>
                <p className="text-xl text-slate-500 font-medium" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t('help.search.noResults', { defaultValue: 'Keine Ergebnisse gefunden' })}
                </p>
                <p className="text-slate-400 mt-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  Versuchen Sie es mit einem anderen Suchbegriff.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Contact Support */}
        <div className="max-w-[1280px] mx-auto px-6 mt-24">
          <div className="bg-white rounded-3xl p-12 text-center relative overflow-hidden shadow-sm border border-slate-200 group">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-emerald-50 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            <div className="relative z-10 max-w-2xl mx-auto">
              <div className="w-20 h-20 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-8 border border-emerald-100 shadow-sm animate-pulse" style={{ animationDelay: '1s' }}>
                <i className="ri-customer-service-2-line text-white text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                {t('help.contact.title')}
              </h2>
              <p className="text-slate-600 text-lg mb-10" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {t('help.contact.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a
                  href={`mailto:${t('common.contactEmail', { defaultValue: 'info@reputrust.ai' })}`}
                  className="px-8 py-4 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white rounded-lg hover:shadow-[0_0_15px_rgba(16,185,129,0.4)] transition-all duration-300 font-bold uppercase tracking-wider shadow-md inline-flex items-center justify-center gap-2"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  <i className="ri-mail-send-line"></i>
                  {t('help.contact.email')}
                </a>
                <a
                  href="tel:+4970263999988"
                  className="px-8 py-4 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all duration-300 font-medium uppercase tracking-wider inline-flex items-center justify-center gap-2 shadow-sm"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  <i className="ri-phone-line"></i>
                  {t('help.contact.phone')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
